import { PublicPageShell } from "./PublicPageShell";
import { cn } from "../cn";
import { useI18n } from "../../i18n";
import {
  getLegalBundle,
  legalEn,
  LEGAL_DOC_ORDER,
  LEGAL_DOC_INDEX,
  type LegalDocId,
} from "../../i18n/legal";

/**
 * Macro #33 · W7 — launch-ready DRAFT legal / privacy surfaces.
 *
 * These are DRAFTS for professional legal review (incl. MiCA applicability).
 * Acceptance Fix Batch #2 — the English legal substance stays canonical but the
 * surface is now fully localized: each Legal page resolves the active locale's
 * bundle (`src/i18n/legal/<locale>.ts`). Policy (FROZEN, Claudio Q5): NO
 * advertising trackers, NO profiling trackers, NO marketing pixels; the
 * cookie-consent banner is intentionally NOT added because only
 * strictly-necessary session cookies are used (technical basis documented in
 * the Cookie Policy). Professional legal review remains an external launch gate.
 */

export type { LegalDocId };
export { LEGAL_DOC_ORDER };

interface LegalDocEntry {
  id: LegalDocId;
  index: string;
  eyebrow: string;
  title: string;
  intro: string;
  sections: { heading: string; body: string[] }[];
}

// Backward-compatible static (English) catalogue — consumed by pure tests and
// as a safe default. The live component localizes via the active-locale bundle.
export const LEGAL_NAV: { id: LegalDocId; label: string }[] = LEGAL_DOC_ORDER.map(
  (id) => ({ id, label: legalEn.nav[id] }),
);

export const LEGAL_DOCS: Record<LegalDocId, LegalDocEntry> = Object.fromEntries(
  LEGAL_DOC_ORDER.map((id) => [
    id,
    { id, index: LEGAL_DOC_INDEX[id], ...legalEn.docs[id] },
  ]),
) as Record<LegalDocId, LegalDocEntry>;

export function legalDocFromRoute(route: string): LegalDocId {
  // #legal → terms (default); #legal/<docId> → that doc.
  if (route === "legal") return "terms";
  if (route.startsWith("legal/")) {
    const id = route.slice("legal/".length).split(/[?#]/)[0].trim() as LegalDocId;
    if (LEGAL_DOC_ORDER.includes(id)) return id;
  }
  return "terms";
}

function LegalNav({
  active,
  nav,
  label,
}: {
  active: LegalDocId;
  nav: Record<LegalDocId, string>;
  label: string;
}) {
  return (
    <nav
      data-testid="legal-nav"
      className="flex flex-wrap gap-2"
      aria-label={label}
    >
      {LEGAL_DOC_ORDER.map((id) => (
        <a
          key={id}
          data-testid={`legal-nav-${id}`}
          href={`#legal/${id}`}
          className={cn(
            "rounded-pill px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] no-underline transition-colors",
            id === active
              ? "bg-neon-teal/15 text-neon-teal border border-neon-teal/50"
              : "border border-border-soft text-text-secondary hover:text-text-primary",
          )}
        >
          {nav[id]}
        </a>
      ))}
    </nav>
  );
}

export function LegalPage({ doc }: { doc: LegalDocId }) {
  const { locale, t } = useI18n();
  const bundle = getLegalBundle(locale);
  const id: LegalDocId = LEGAL_DOC_ORDER.includes(doc) ? doc : "terms";
  const d = bundle.docs[id];
  const navLabel = t("legal.navAria");

  return (
    <PublicPageShell
      testId={`legal-page-${id}`}
      index={LEGAL_DOC_INDEX[id]}
      eyebrow={d.eyebrow}
      title={d.title}
      intro={d.intro}
      footer={<LegalNav active={id} nav={bundle.nav} label={navLabel} />}
    >
      <div
        data-testid="legal-draft-note"
        className="neon-glass mb-8 px-4 py-3 font-mono text-[11px] leading-relaxed text-text-secondary"
      >
        {bundle.draftNote}
      </div>

      <div className="mb-10">
        <LegalNav active={id} nav={bundle.nav} label={navLabel} />
      </div>

      <div className="flex flex-col gap-7 max-w-[760px]">
        {d.sections.map((s, i) => (
          <section key={i} data-testid={`legal-section-${id}-${i}`}>
            <h2 className="font-display text-[15px] font-semibold tracking-[0.02em] text-text-primary">
              {s.heading}
            </h2>
            {s.body.map((p, j) => (
              <p
                key={j}
                className="mt-2 font-display text-[13px] leading-[1.7] text-text-secondary"
              >
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>
    </PublicPageShell>
  );
}
